import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@clerk/react";
import { useUpdateContract, getGetCoachQueryKey, getGetContractQueryKey, getListContractsQueryKey } from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, ChevronLeft, ChevronRight, ShieldOff, Calculator } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { handleApiError } from "@/lib/handleApiError";
import { cn } from "@/lib/utils";
import {
  TERMINATION_OFFBOARDING_STEPS,
  TERMINATION_TYPE_LABELS,
  type TerminationOffboardingStep,
  buildTerminationOffboardingNotes,
  canAdvanceTerminationStep,
  defaultTerminationOffboardingState,
  estimateTerminationBuyout,
} from "@/lib/terminationOffboarding";
import type { TerminationType } from "@workspace/buyout";
import { DEFAULT_TERMINATION_RULES } from "@workspace/buyout";

interface ContractLike {
  id: number;
  coachId: number;
  coachName?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  annualSalary?: number | string | null;
  buyoutClause?: number | string | null;
  buyoutSchedule?: unknown;
  terminationRules?: unknown;
  mitigationRequired?: boolean | null;
  terminationConditions?: string | null;
  notes?: string | null;
  financialsMasked?: boolean;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contract: ContractLike;
}

export function TerminateOffboardingDialog({ open, onOpenChange, contract }: Props) {
  const { toast } = useToast();
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  const updateContract = useUpdateContract();
  const [step, setStep] = useState<TerminationOffboardingStep>(1);
  const [submitting, setSubmitting] = useState(false);
  const [state, setState] = useState(() =>
    defaultTerminationOffboardingState({
      mitigationEnabled: Boolean(contract.mitigationRequired),
    }),
  );

  const [portalEnabled, setPortalEnabled] = useState(false);

  useEffect(() => {
    if (!open || !contract.coachId) return;
    let cancelled = false;
    void (async () => {
      try {
        const token = await getToken();
        const res = await fetch(`/api/coaches/${contract.coachId}`, {
          credentials: "include",
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        });
        if (!res.ok) return;
        const data = (await res.json()) as { portalAccessEnabled?: boolean };
        if (!cancelled) setPortalEnabled(Boolean(data.portalAccessEnabled));
      } catch {
        /* ignore — revoke still works if user opts in */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, contract.coachId, getToken]);

  useEffect(() => {
    if (!open) return;
    setStep(1);
    setSubmitting(false);
    setState(
      defaultTerminationOffboardingState({
        mitigationEnabled: Boolean(contract.mitigationRequired),
        revokePortalAccess: portalEnabled,
      }),
    );
  }, [open, contract.id, contract.mitigationRequired, portalEnabled]);

  const breakdown = useMemo(
    () =>
      estimateTerminationBuyout({
        startDate: contract.startDate,
        endDate: contract.endDate,
        annualSalary: contract.annualSalary,
        buyoutClause: contract.buyoutClause,
        buyoutSchedule: (contract.buyoutSchedule ?? []) as Parameters<
          typeof estimateTerminationBuyout
        >[0]["buyoutSchedule"],
        terminationRules:
          (contract.terminationRules as typeof DEFAULT_TERMINATION_RULES | null) ??
          DEFAULT_TERMINATION_RULES,
        terminationDate: state.terminationDate,
        terminationType: state.terminationType,
        mitigationEnabled: state.mitigationEnabled,
        mitigationAmount: state.mitigationAmount,
      }),
    [contract, state],
  );

  const estimatedAmount = breakdown?.finalAmountOwed ?? null;
  const financialsMasked = Boolean(contract.financialsMasked);
  const canNext = canAdvanceTerminationStep(step, state);
  const coachLabel = contract.coachName?.trim() || "this coach";

  const goNext = () => {
    if (!canNext) return;
    setStep((s) => Math.min(4, s + 1) as TerminationOffboardingStep);
  };
  const goBack = () => setStep((s) => Math.max(1, s - 1) as TerminationOffboardingStep);

  const finalize = async () => {
    if (!canAdvanceTerminationStep(4, state) || submitting) return;
    setSubmitting(true);
    try {
      const notes = buildTerminationOffboardingNotes({
        state,
        estimatedBuyout: financialsMasked ? null : estimatedAmount,
        priorNotes: contract.terminationConditions,
      });

      await new Promise<void>((resolve, reject) => {
        updateContract.mutate(
          {
            id: contract.id,
            data: {
              status: "terminated",
              terminationConditions: notes,
            } as Parameters<typeof updateContract.mutate>[0]["data"],
          },
          {
            onSuccess: () => resolve(),
            onError: (err) => reject(err),
          },
        );
      });

      const token = await getToken();
      const authHeaders: HeadersInit = {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      };

      if (state.revokePortalAccess && portalEnabled) {
        const res = await fetch(`/api/coaches/${contract.coachId}/portal-access`, {
          method: "PATCH",
          headers: authHeaders,
          credentials: "include",
          body: JSON.stringify({
            portalAccessEnabled: false,
            portalStatus: "disabled",
          }),
        });
        if (!res.ok) {
          toast({
            title: "Contract terminated — portal revoke failed",
            description: "Revoke coach portal access manually from the coach profile.",
            variant: "destructive",
          });
        }
      }

      if (state.revokeGuestAccess) {
        try {
          const listRes = await fetch(`/api/contracts/${contract.id}/guests`, {
            credentials: "include",
            headers: token ? { Authorization: `Bearer ${token}` } : undefined,
          });
          if (listRes.ok) {
            const guests = (await listRes.json()) as Array<{ id: number; status: string }>;
            const active = guests.filter((g) => g.status === "pending" || g.status === "active");
            await Promise.all(
              active.map((g) =>
                fetch(`/api/contracts/${contract.id}/guests/${g.id}`, {
                  method: "DELETE",
                  credentials: "include",
                  headers: token ? { Authorization: `Bearer ${token}` } : undefined,
                }),
              ),
            );
          }
        } catch {
          toast({
            title: "Contract terminated — guest revoke incomplete",
            description: "Review guest access on this contract and revoke any remaining invites.",
            variant: "destructive",
          });
        }
      }

      toast({
        title: "Contract terminated",
        description: `Offboarding recorded for ${coachLabel}.`,
      });
      queryClient.invalidateQueries({ queryKey: getGetContractQueryKey(contract.id) });
      queryClient.invalidateQueries({ queryKey: getListContractsQueryKey() });
      queryClient.invalidateQueries({ queryKey: getGetCoachQueryKey(contract.coachId) });
      onOpenChange(false);
    } catch (err) {
      handleApiError(toast, err, { fallbackTitle: "Could not terminate contract" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!submitting) onOpenChange(next);
      }}
    >
      <DialogContent
        className="max-w-xl max-h-[90vh] overflow-y-auto"
        data-testid="terminate-offboarding-dialog"
        onInteractOutside={(e) => {
          if (submitting) e.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-destructive">
            <AlertTriangle className="h-5 w-5" />
            Terminate contract
          </DialogTitle>
          <DialogDescription>
            Termination is permanent for this employment status. Walk the offboarding checklist
            before the status changes.
          </DialogDescription>
        </DialogHeader>

        <ol
          className="flex items-stretch gap-1 rounded-lg border bg-muted/30 px-2 py-2"
          aria-label="Termination offboarding steps"
          data-testid="terminate-offboarding-stepper"
        >
          {TERMINATION_OFFBOARDING_STEPS.map((s) => {
            const isCurrent = s.id === step;
            const isDone = s.id < step;
            return (
              <li key={s.id} className="flex-1 min-w-0 text-center">
                <div
                  className={cn(
                    "text-[11px] sm:text-xs font-medium truncate px-1 py-1 rounded",
                    isCurrent && "bg-background shadow-sm text-foreground",
                    isDone && !isCurrent && "text-muted-foreground",
                    !isDone && !isCurrent && "text-muted-foreground/70",
                  )}
                  data-testid={`terminate-step-${s.id}`}
                >
                  {s.id}. {s.label}
                </div>
              </li>
            );
          })}
        </ol>

        {step === 1 && (
          <div className="space-y-4" data-testid="terminate-step-reason">
            <p className="text-sm text-muted-foreground">
              Confirm how and when this contract with <span className="font-medium text-foreground">{coachLabel}</span> ends.
            </p>
            <div className="space-y-2">
              <Label htmlFor="termination-effective-date">Effective termination date</Label>
              <Input
                id="termination-effective-date"
                type="date"
                value={state.terminationDate}
                onChange={(e) => setState((s) => ({ ...s, terminationDate: e.target.value }))}
                data-testid="input-termination-date"
              />
            </div>
            <div className="space-y-2">
              <Label>Termination type</Label>
              <Select
                value={state.terminationType}
                onValueChange={(v) =>
                  setState((s) => ({ ...s, terminationType: v as TerminationType }))
                }
              >
                <SelectTrigger data-testid="select-termination-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(Object.keys(TERMINATION_TYPE_LABELS) as TerminationType[]).map((key) => (
                    <SelectItem key={key} value={key}>
                      {TERMINATION_TYPE_LABELS[key]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="termination-reason">Reason / notes (optional)</Label>
              <Textarea
                id="termination-reason"
                value={state.reason}
                onChange={(e) => setState((s) => ({ ...s, reason: e.target.value }))}
                placeholder="Internal context for HR, legal, and finance…"
                rows={3}
                data-testid="textarea-termination-reason"
              />
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4" data-testid="terminate-step-buyout">
            <div className="rounded-lg border bg-card p-4 space-y-2">
              <div className="flex items-center gap-2 text-sm font-medium">
                <Calculator className="h-4 w-4 text-muted-foreground" />
                Estimated buyout
              </div>
              {financialsMasked ? (
                <p className="text-sm text-muted-foreground">Financials are masked in this session.</p>
              ) : estimatedAmount != null ? (
                <p className="text-2xl font-semibold tabular-nums" data-testid="text-estimated-buyout">
                  ${Math.round(estimatedAmount).toLocaleString()}
                </p>
              ) : (
                <p className="text-sm text-muted-foreground">
                  Unable to estimate from this contract’s terms. Review the Buyout tab before
                  finalizing.
                </p>
              )}
              {breakdown?.explanation && !financialsMasked && (
                <p className="text-xs text-muted-foreground">{breakdown.explanation}</p>
              )}
            </div>

            <div className="space-y-3">
              <Label className="text-sm font-medium">Does a buyout obligation apply?</Label>
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant={state.buyoutObligationApplies === true ? "default" : "outline"}
                  onClick={() => setState((s) => ({ ...s, buyoutObligationApplies: true }))}
                  data-testid="button-buyout-applies-yes"
                >
                  Yes — obligation applies
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant={state.buyoutObligationApplies === false ? "default" : "outline"}
                  onClick={() => setState((s) => ({ ...s, buyoutObligationApplies: false }))}
                  data-testid="button-buyout-applies-no"
                >
                  No — none owed
                </Button>
              </div>
            </div>

            <div className="flex items-start gap-2">
              <Checkbox
                id="buyout-reviewed"
                checked={state.buyoutReviewed}
                onCheckedChange={(v) => setState((s) => ({ ...s, buyoutReviewed: v === true }))}
                data-testid="checkbox-buyout-reviewed"
              />
              <Label htmlFor="buyout-reviewed" className="text-sm font-normal leading-snug">
                I reviewed the buyout estimate (or confirmed it is unavailable) before terminating.
              </Label>
            </div>

            {state.buyoutObligationApplies === true && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="mitigation-enabled"
                    checked={state.mitigationEnabled}
                    onCheckedChange={(v) =>
                      setState((s) => ({ ...s, mitigationEnabled: v === true }))
                    }
                    data-testid="checkbox-mitigation"
                  />
                  <Label htmlFor="mitigation-enabled" className="text-sm font-normal">
                    Apply mitigation offset
                  </Label>
                </div>
                {state.mitigationEnabled && (
                  <div className="space-y-1">
                    <Label htmlFor="mitigation-amount" className="text-xs">
                      Mitigation amount
                    </Label>
                    <Input
                      id="mitigation-amount"
                      type="number"
                      min={0}
                      value={state.mitigationAmount}
                      onChange={(e) =>
                        setState((s) => ({
                          ...s,
                          mitigationAmount: Number(e.target.value) || 0,
                        }))
                      }
                      data-testid="input-mitigation-amount"
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4" data-testid="terminate-step-access">
            <p className="text-sm text-muted-foreground">
              Cut access and capture offboarding follow-ups. Recommended defaults revoke portal and
              guest access.
            </p>

            <div className="rounded-lg border p-3 space-y-3">
              <div className="flex items-start gap-2">
                <Checkbox
                  id="revoke-portal"
                  checked={state.revokePortalAccess}
                  onCheckedChange={(v) =>
                    setState((s) => ({ ...s, revokePortalAccess: v === true }))
                  }
                  data-testid="checkbox-revoke-portal"
                />
                <div>
                  <Label htmlFor="revoke-portal" className="text-sm font-medium">
                    Revoke coach portal access
                  </Label>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {portalEnabled
                      ? "Portal is currently enabled for this coach."
                      : "Portal is not currently enabled; this will still record your intent."}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-2">
                <Checkbox
                  id="revoke-guests"
                  checked={state.revokeGuestAccess}
                  onCheckedChange={(v) =>
                    setState((s) => ({ ...s, revokeGuestAccess: v === true }))
                  }
                  data-testid="checkbox-revoke-guests"
                />
                <div>
                  <Label htmlFor="revoke-guests" className="text-sm font-medium">
                    Revoke contract guest / counsel access
                  </Label>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Ends pending and active guest invites on this contract.
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <p className="text-sm font-medium flex items-center gap-2">
                <ShieldOff className="h-4 w-4 text-muted-foreground" />
                Offboarding checklist
              </p>
              {(
                [
                  ["checklistReturnEquipment", "Equipment / campus property return", "checkbox-equipment"],
                  ["checklistHrPayroll", "HR / payroll notified", "checkbox-hr"],
                  ["checklistBenefits", "Benefits / insurance follow-up", "checkbox-benefits"],
                ] as const
              ).map(([key, label, testId]) => (
                <div key={key} className="flex items-start gap-2">
                  <Checkbox
                    id={key}
                    checked={state[key]}
                    onCheckedChange={(v) => setState((s) => ({ ...s, [key]: v === true }))}
                    data-testid={testId}
                  />
                  <Label htmlFor={key} className="text-sm font-normal">
                    {label}
                  </Label>
                </div>
              ))}
            </div>
          </div>
        )}

        {step === 4 && (
          <div className="space-y-4" data-testid="terminate-step-confirm">
            <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4 space-y-2 text-sm">
              <p>
                You are about to mark the contract for <strong>{coachLabel}</strong> as{" "}
                <strong>terminated</strong> effective <strong>{state.terminationDate}</strong> (
                {TERMINATION_TYPE_LABELS[state.terminationType]}).
              </p>
              <ul className="list-disc pl-5 space-y-1 text-muted-foreground">
                <li>
                  Buyout obligation:{" "}
                  {state.buyoutObligationApplies ? "applies" : "none owed"}
                  {!financialsMasked && estimatedAmount != null
                    ? ` (est. $${Math.round(estimatedAmount).toLocaleString()})`
                    : ""}
                </li>
                <li>Portal revoke: {state.revokePortalAccess ? "yes" : "no"}</li>
                <li>Guest revoke: {state.revokeGuestAccess ? "yes" : "no"}</li>
              </ul>
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm-terminate">
                Type <span className="font-mono font-semibold">TERMINATE</span> to confirm
              </Label>
              <Input
                id="confirm-terminate"
                value={state.confirmTyped}
                onChange={(e) => setState((s) => ({ ...s, confirmTyped: e.target.value }))}
                autoComplete="off"
                placeholder="TERMINATE"
                data-testid="input-confirm-terminate"
              />
            </div>
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-0">
          {step > 1 ? (
            <Button
              type="button"
              variant="outline"
              onClick={goBack}
              disabled={submitting}
              data-testid="button-terminate-back"
            >
              <ChevronLeft className="h-4 w-4 mr-1" /> Back
            </Button>
          ) : (
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              disabled={submitting}
              data-testid="button-terminate-cancel"
            >
              Cancel
            </Button>
          )}
          {step < 4 ? (
            <Button
              type="button"
              onClick={goNext}
              disabled={!canNext}
              data-testid="button-terminate-next"
            >
              Next <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          ) : (
            <Button
              type="button"
              variant="destructive"
              onClick={() => void finalize()}
              disabled={!canNext || submitting}
              data-testid="button-terminate-finalize"
            >
              {submitting ? "Terminating…" : "Finalize termination"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
